import React, { useState, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import {
  DataGrid,
  GridToolbarQuickFilter,
  GridLinkOperator,
  GridRenderCellParams,
  GridColumns,
} from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { getAllDownloadsReport, sendEmailRequest } from "../../../redux/slices/downloads";
import { Button } from "@mui/material";
import moment from "moment";
import ApiStateModal from "../../../components/reusable-ui/basic-modal";
import { isMobileDevice } from "../../../helpers/device-info-helper";
import SendEmailModal from "../send-email";
import { DATE_TIME_FORMATS, EMAIL_NOT_VERIFIED, EMAIL_VALIDATION_ERROR, MESSAGE_TYPE, SYSTEM } from "../../../data/constants";
import { getAllUsers, getUserDetails } from "../../../redux/slices";
import { IS_SUPPORT_LOGIN } from "../../../data/local-storage-constants";
import { getLocalStorage } from "../../../utils";
import { getRowsAccordingToUserType } from "../../../helpers/user-based-report";
import { toastError } from "../../../utils/react-toaster";
import { getUpdatedUserDetails } from "../../../helpers/user-info-helpers";
import { isSupportLogin } from "../../../helpers/common-helper";
import { isEmpty } from "lodash";

function QuickSearchToolbar() {
  return (
    <Box
      sx={{
        p: 0.5,
        pb: 0,
        m: 1,
      }}
    >
      <GridToolbarQuickFilter
        placeholder="Search by Report Type, Report Name, Raised By and Status"
        quickFilterParser={(searchInput: string) => {
          const ss = searchInput
            .split(",")
            ?.map((value) => value.trim())
            ?.filter((value) => value !== "");
          return ss;
        }}
      />
    </Box>
  );
}

export const dataGridStyle = {
  height: isMobileDevice() ? "calc(100vh - 200px)" : "calc(100vh - 138px)",
  display: "flex",
  backgroundColor: "#fff",
  ".MuiDataGrid-selectedRowCount": {
    visibility: "hidden!important",
  },
  ".MuiTablePagination-selectLabel": {
    visibility: "hidden!important",
  },
  ".MuiDataGrid-menuIconButton": {
    visibility: "hidden!important",
  },
  "div[class*='-toolbarQuickFilter']": {
    width: "40%",
    marginBottom: "10px",
  },
  ".hide-action": {
    display: "none!important",
  },
  ".MuiDataGrid-row:hover > .MuiDataGrid-cell > .hide-action": {
    display: "flex!important",
  },
  ".MuiDataGrid-row:focus > .MuiDataGrid-cell > .hide-action": {
    display: "flex!important",
  },
  ".MuiDataGrid-columnHeaders": {
    background: "#dedede",
  },
  ".MuiDataGrid-columnHeaderTitle": {
    fontWeight: "600",
  },
  ".MuiDataGrid-cell:focus ": {
    outline: "none!important",
  },
  ".MuiDataGrid-iconSeparator": {
    display: "none",
  },
  ".Mui-selected": {
    backgroundColor: "white!important",
  },
  ".MuiDataGrid-main": {
    overflowY: "auto",
  },
};
const row: any = {};
const data = { rows: [row] };
type Row = typeof data.rows[number];
let rowDetails = {};
const VISIBLE_FIELDS = [
  "name",
  "group",
  "createdDate",
  "userName",
  "reportStatus",
  "periodString",
  "links",
];

export default function DownloadPageComponent() {
  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [apiStateModalConfig, setApiStateModalConfig] = useState<any>({
    show: false,
    message: "",
    modalType: "",
  });
  const dispatch = useDispatch();
  const { userData: { businessUserId,  contactInfo: { email = '', verifiedEmail = '', name: { firstName = '', lastName = '' } = {} } = {} } = {} } = useSelector((state: any) => ({
    userData: state.businessReducer?.userData,
  }));
  const allUsersDataMap = useSelector(
    (state: any) => state.outletStatusReducer.allUsersMap
  );
  const reportRow = useSelector(
    (state: any) => state.savedReportReducer?.allDownloadedReports || []
  );
  const isFetchingReportsData = useSelector(
    (state: any) => state.savedReportReducer?.isFetchingReportsData
  );
  data.rows = isSupportLogin() && Array.isArray(reportRow) && reportRow.length ? reportRow : (getRowsAccordingToUserType(reportRow, email, firstName + " " + lastName, businessUserId) || []);

  useEffect(() => {
    if (isEmpty(allUsersDataMap)) {
      dispatch(getAllUsers());
    }
    fetchReports();
  }, []);

  function fetchReports() {
    dispatch(getAllDownloadsReport());
  }

  const emailReport = (reportId: any) => {
    const selectedRow = data?.rows?.filter(
      (row) => row?.links?.[0]?.reportId == reportId
    );
    setShowEmailModal(true);
    rowDetails = selectedRow ? selectedRow[0] : {};
    validateErrorAndSendEmail()
  };

  function validateErrorAndSendEmail() {
    const { email, verifiedEmail } = getUpdatedUserDetails();
    if (!email) {
      toastError(EMAIL_VALIDATION_ERROR);
    }
    else if (!verifiedEmail) {
      toastError(EMAIL_NOT_VERIFIED);
    }
    else {
      const payloadToSend = {
        rowDetails,
        emailList: [verifiedEmail]
      }
      const callback = (response: any) => {
        let message: string, modalType: string;
        if (response.code) {
          message = "Something Went Wrong";
          modalType = MESSAGE_TYPE.error;
        } else {
          message = "Report has been sent to given email(s)";
          modalType = MESSAGE_TYPE.success;
        }
        setApiStateModalConfig({
          ...apiStateModalConfig,
          message,
          modalType,
          show: true,
        });
      };
      dispatch(sendEmailRequest({ payloadToSend, callback }));
    }
  }

  const periodStringFormatter = (value: any) => {
    let periodArr = value ? value.split(",") : value;
    if (Array.isArray(periodArr) && periodArr[0].length > 8) {
      return `${moment(periodArr[0]).format(
        DATE_TIME_FORMATS.dayMonthYear
      )} - ${moment(periodArr[periodArr.length - 1]).format(
        DATE_TIME_FORMATS.dayMonthYear
      )}`;
    } else if (Array.isArray(periodArr)) {
      const formattedMonth = periodArr.map((month) => {
        return moment(month).format(DATE_TIME_FORMATS.monthYear);
      });
      return formattedMonth.join(", ");
    } else return value;
  };

  function getUserName(businessUserId: string) {
    if (businessUserId === SYSTEM) return SYSTEM;
    if (!businessUserId) {
    return "";
    }
    const user = allUsersDataMap[businessUserId];
    return (user?.name?.firstName || "") + " " + (user?.name?.lastName || "");
  }

  const reportColumns = useMemo<GridColumns<Row>>(
    () => [
      {
        field: "group",
        headerName: "Report Type",
        width: 200,
        valueFormatter: (params) => params.value || "Custom Report",
      },
      {
        field: "name",
        headerName: "Report Name",
        width: 220,
      },
      {
        field: "periodString",
        headerName: "Date Range",
        width: 200,
        valueFormatter: (params) => periodStringFormatter(params?.value),
      },
      {
        field: "createdDate",
        headerName: "Request Date",
        width: 170,
        valueFormatter: (params) =>
          moment(params?.value).format(DATE_TIME_FORMATS.yearMonthDayTime),
      },
      {
        field: "userName",
        headerName: "Raised By",
        width: 180,
        valueFormatter: (params) => {
          const rowData = params.api.getRow(params.id);
          return params.value || getUserName(rowData?.businessUserId as string || '')
        },
      },
      {
        field: "reportStatus",
        headerName: "Status",
        width: 130,
        type: "singleSelect",
        valueOptions: ["In-Progress", "Completed", "Closed"],
        renderCell: (params: GridRenderCellParams<String>) => (
          <strong>
            <Button
              variant="outlined"
              size="small"
              tabIndex={params.hasFocus ? 0 : -1}
              color={
                params.value === "In-Progress"
                  ? "primary"
                  : params.value === "Completed"
                    ? "success"
                    : "error"
              }
            >
              {params.value}
            </Button>
          </strong>
        ),
      },
      {
        field: "links",
        headerName: "Actions",
        type: "singleSelect",
        width: 200,
        renderCell: (params: GridRenderCellParams<any>) => {
          const note = params.row?.note;
          return (
            <div
              className={`justify-between w-full !flex ${isMobileDevice() ? "" : "hide-action"
                } ${params?.value?.[0] ? "" : "pointer-events-none !opacity-25"}`}
              style={{ display: "contents" }}
            >
              {!note && (
                <div className="dwnld w-[50%]">
                  <div
                    className="rista-sprite ic-item-download pl-20 cursor-pointer"
                    onClick={() =>
                      (window.location.href = `${params?.value[0]?.url}`)
                    }
                  >
                    <span className="text-12">Download</span>
                  </div>
                </div>
              )}
              {!note && (
                <div
                  className="email color-black cursor-pointer w-[50%]"
                  onClick={() => {
                    emailReport(params?.value?.[0]?.reportId);
                  }}
                >
                  <div className="rista-sprite ic-item-email-rocket">
                    <span className="text-12 pl-20">E-Mail</span>
                  </div>
                </div>
              )}
              {note && (
                <div className="notice text-12">{note}</div>
              )}
            </div>
          );
        },
      },
    ],
    []
  );

  const columns = useMemo(
    () =>
      reportColumns
        ?.filter((column) => VISIBLE_FIELDS.includes(column.field))
        ?.map((column: any) => {
          if (column.field === "links" || column.field === "periodString") {
            return { ...column, getApplyQuickFilterFn: undefined };
          }
          return column;
        }),
    [reportColumns]
  );
  return (
    <section className="download-container mx-20 mt-20 ">
      <Box sx={dataGridStyle}>
        <DataGrid
          loading={isFetchingReportsData}
          autoHeight
          {...data}
          columns={columns}
          initialState={{
            filter: {
              filterModel: {
                items: [],
                quickFilterLogicOperator: GridLinkOperator.Or,
              },
            },
            pagination: {
              pageSize: 20,
            },
          }}
          components={{ Toolbar: QuickSearchToolbar }}
        />
      </Box>
      <button className="fixed right-30 top-[116px] refresh-btn border border-[#397EF5] p-6 cursor-pointer rounded-[6px] hover:bg-[#E4E5EC] sm:hidden text-[#397EF5] z-[1]" onClick={fetchReports}>Refresh</button>
      {apiStateModalConfig.show && (
        <ApiStateModal
          open={apiStateModalConfig.show}
          handleClose={() =>
            setApiStateModalConfig({ ...apiStateModalConfig, show: false })
          }
          subText={apiStateModalConfig.message}
          modalType={apiStateModalConfig.modalType}
        ></ApiStateModal>
      )}
    </section>
  );
}
